// apps/web/src/pages/author/AISuggestionContext.tsx
//
// Phase 10 Plan 12 Task 1 — AI-suggestion context for the Author UI prefill // sanitize-allow: [A-Z]{3,5}-[0-9]{2}
// state (Surface 3 per 10-UI-SPEC §"Surface 3 — Author UI prefill state"
// lines 187-209).
//
// Provides the two flags the ExtractionDraftBanner reads and the
// ReadonlySet<string> of RHF paths the AISuggestedField wrapper consults.
// Both pieces of state are computed ONCE from the loaded recipe in
// RecipeEditor and threaded down via this context — no extra fetch, no
// per-render Set allocation (10-UI-SPEC §"Performance Contract" lines
// 856-858: the provider value MUST be memoized by the producer).
//
// GRACEFUL DEGRADE (10-UI-SPEC §<AISuggestedField> + plan 10-12 truths):
// when no provider is mounted, useExtractionDraftFlags returns the
// all-false default and useAISuggestedPaths returns an empty Set — so an
// AISuggestedField rendered outside the provider is a pure pass-through and
// the banner never shows. This is the contract the Phase 8 RecipeEditor
// tests rely on: wrapping a field with no provider preserves the unwrapped
// behavior exactly.

import { createContext, useContext } from "react";

// ExtractionDraftFlags — the banner-driving flags, sourced from the loaded
// recipe row (out-of-band sidecar columns per the Plan 10-02 schema; the
// RecipeDefinitionV1 body does not carry them). sourceUrlOrHash is the
// human-facing attribution string (sourceUrl when present, else the content
// hash) the banner + tooltip interpolate.
export interface ExtractionDraftFlags {
  extraction_partial: boolean;
  is_ai_extracted_unreviewed: boolean;
  sourceUrlOrHash?: string;
}

// AISuggestionContextValue — the whole annotation-layer state for the
// editing session. flags drive the banner; suggestedPaths drive the
// per-field annotation. Both are stable for the session (the producer
// memoizes them over recipe.extraction_meta).
export interface AISuggestionContextValue {
  flags: ExtractionDraftFlags;
  suggestedPaths: ReadonlySet<string>;
}

// Default flags when no provider is mounted — nothing is flagged.
const DEFAULT_FLAGS: ExtractionDraftFlags = {
  extraction_partial: false,
  is_ai_extracted_unreviewed: false,
};

// Default suggested-paths when no provider is mounted — empty set. Frozen so
// the singleton reference is stable across calls (a fresh Set() per render
// would defeat the no-op-wrapper render-count contract).
const EMPTY_PATHS: ReadonlySet<string> = new Set<string>();

const AISuggestionContext = createContext<AISuggestionContextValue | null>(null);

export interface AISuggestionProviderProps {
  value: AISuggestionContextValue;
  children: React.ReactNode;
}

// AISuggestionProvider — thin context provider. The `value` it receives MUST
// already be memoized by the caller (RecipeEditor uses useMemo over the
// loaded recipe.extraction_meta); this provider does NOT memoize on its
// behalf, matching the 10-UI-SPEC performance contract that the producer
// owns reference stability.
export function AISuggestionProvider({
  value,
  children,
}: AISuggestionProviderProps): React.ReactElement {
  return <AISuggestionContext.Provider value={value}>{children}</AISuggestionContext.Provider>;
}

// useExtractionDraftFlags — read the banner flags from context. Returns the
// all-false default when no provider is present (graceful degrade).
export function useExtractionDraftFlags(): ExtractionDraftFlags {
  const ctx = useContext(AISuggestionContext);
  return ctx?.flags ?? DEFAULT_FLAGS;
}

// useAISuggestedPaths — read the AI-suggested RHF-path set from context.
// Returns a stable empty Set when no provider is present (graceful degrade).
export function useAISuggestedPaths(): ReadonlySet<string> {
  const ctx = useContext(AISuggestionContext);
  return ctx?.suggestedPaths ?? EMPTY_PATHS;
}
